import type { AdminUser, DashboardStats, StatBucket } from './types'

// The Yep back-office is reached via the Vite proxy (/admin/* -> Apache). Session
// is cookie-based, so every call uses credentials: 'include'.
const BASE = '/admin'

export class ApiError extends Error {
  status: number
  constructor(message: string, status = 0) {
    super(message)
    this.status = status
  }
}

// Broadcast an expired session so the app can drop back to the login screen.
// Debounced: a single page can fire many parallel calls that all 401 at once.
let lastUnauthorizedAt = 0
export function notifyUnauthorized() {
  const now = Date.now()
  if (now - lastUnauthorizedAt < 1500) return
  lastUnauthorizedAt = now
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('gsm:unauthorized'))
}

async function asJson<T>(res: Response): Promise<T> {
  const text = await res.text()
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError('Unexpected non-JSON response', res.status)
  }
}

// Extract the first balanced {...} object from a string (the Yep login handler can append a
// second status object after the real payload, e.g. `{user}` or `{status:401}{status:404}`).
function firstJsonObject(text: string): Record<string, unknown> | null {
  let depth = 0
  let start = -1
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === '{') {
      if (depth === 0) start = i
      depth++
    } else if (ch === '}') {
      depth--
      if (depth === 0 && start >= 0) {
        try {
          return JSON.parse(text.slice(start, i + 1)) as Record<string, unknown>
        } catch {
          return null
        }
      }
    }
  }
  return null
}

/**
 * Yep login — form-encoded POST. The backend is quirky: on wrong credentials it returns
 * `{"status":401,...}{"status":404,...}` with an HTTP 404 (not 401); on success it returns the
 * user object. So we parse the first JSON object rather than trust the HTTP status.
 */
export async function login(username: string, password: string): Promise<AdminUser> {
  const body = new URLSearchParams({ username, password })
  const res = await fetch(`${BASE}/rest/command/user/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
    body,
    credentials: 'include',
  })
  const text = await res.text()
  const obj = firstJsonObject(text)

  if (res.status === 401 || (obj && obj.status === 401)) {
    throw new ApiError('Credenziali non valide', 401)
  }
  if (obj && typeof obj.username === 'string' && obj.username) {
    return obj as unknown as AdminUser
  }
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  throw new ApiError('Login fallito', res.status)
}

export async function logout(): Promise<void> {
  await fetch(`${BASE}/rest/command/user/logout`, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  }).catch(() => {})
}

/** Current session user (200 -> user, else not authenticated). */
export async function currentUser(): Promise<AdminUser | null> {
  try {
    const res = await fetch(`${BASE}/rest/command/currentuser`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'include',
    })
    if (!res.ok) return null
    const u = await asJson<AdminUser>(res)
    return u?.username ? u : null
  } catch {
    return null
  }
}

/** Dashboard stats for a date range (YYYY-MM-DD). */
export async function getStats(startDate: string, endDate: string): Promise<DashboardStats> {
  const res = await fetch(`${BASE}/actions/stats?startDate=${startDate}&endDate=${endDate}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  // The backend serializes numeric aggregates as strings in some cases (PDO returns numeric
  // columns as strings). Coercing here means every consumer can sum with `+` safely — otherwise
  // `0 + "2" + "2"` concatenates into a serial-looking string and percentages divide to 0%.
  const num = (v: unknown): number => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }
  const buckets = (arr?: StatBucket[]): StatBucket[] =>
    (arr ?? []).map((b) => ({ label: b.label, value: num(b.value) }))
  const raw = await asJson<DashboardStats>(res)
  return {
    bySource: buckets(raw.bySource),
    byRate: buckets(raw.byRate),
    byTransport: buckets(raw.byTransport),
    byPaymentMode: buckets(raw.byPaymentMode),
    byDuration: buckets(raw.byDuration),
    byCountry: buckets(raw.byCountry),
    total: num(raw.total),
    timeline: {
      labels: raw.timeline?.labels ?? [],
      datasets: (raw.timeline?.datasets ?? []).map((d) => ({
        label: d.label,
        data: (d.data ?? []).map(num),
        tension: d.tension,
      })),
    },
  }
}

export async function getNewsletterCount(): Promise<number> {
  const res = await fetch(`${BASE}/actions/exportNewsletter?count=true`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  const d = await asJson<{ count: number }>(res)
  return d.count
}

export function newsletterExportUrl(): string {
  return `${BASE}/actions/exportNewsletter`
}

/* ── Confirmation-email status + resend (per reservation) ── */
export interface EmailLogEntry {
  id: number
  status: string // PENDING | SENT | FAILED
  toEmail: string
  subject: string | null
  emailType: string | null
  attempts: number
  error: string | null
  sentTime: number | null
  createdTime: number
}

/** Email send-log history for one reservation (most recent first). */
export async function getReservationEmailLogs(code: string): Promise<EmailLogEntry[]> {
  const res = await fetch(`${BASE}/actions/emailStatus?code=${encodeURIComponent(code)}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  const d = await asJson<{ logs: EmailLogEntry[] }>(res)
  return d.logs ?? []
}

/** Re-send the confirmation/cancellation email for a reservation. */
export async function resendReservationEmail(code: string): Promise<{ ok: boolean; message: string }> {
  const res = await fetch(`${BASE}/actions/resendEmail?code=${encodeURIComponent(code)}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  return asJson<{ ok: boolean; message: string }>(res)
}

/** Latest email status per reservation code, in one call — for the list dot. */
export async function getReservationEmailStatuses(codes: string[]): Promise<Record<string, string>> {
  if (!codes.length) return {}
  const res = await fetch(`${BASE}/actions/emailStatusBulk?codes=${encodeURIComponent(codes.join(','))}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  const d = await asJson<{ statuses: Record<string, string> }>(res)
  return d.statuses ?? {}
}

/* ── Availability calendar grid (per-day capacity vs. occupancy, per transport/service) ── */
export interface AvailabilityInfo {
  type: 'TRANSPORT' | 'SERVICE'
  id: number
  maxAllowed: number
  maxOnSite?: number
  closed: boolean
  onSiteClosed?: boolean
  // transport: { online, onsite }; service: a plain count
  book: { online: number; onsite: number } | number
  inAndOut?: { in: number; out: number; still: number }
  occupiedParkingSpaces?: number
}
/** Keyed by day "YYYY-MM-DD". */
export type AvailabilityGrid = Record<string, { info: AvailabilityInfo[] }>

export async function getAvailabilityGrid(startDate: string, endDate: string): Promise<AvailabilityGrid> {
  const res = await fetch(`${BASE}/actions/availabilityCalendarGrid?startDate=${startDate}&endDate=${endDate}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  return asJson<AvailabilityGrid>(res)
}

/* ── Per-day editable availability (the day modal) ── */
export interface AvailabilityDayItem {
  referenceId: number
  referenceType: 'TRANSPORT' | 'MAXONSITEPAYMENTAVAILABILITY' | 'SERVICE'
  label: string
  quantity: number | null // override; null = use default
  defaultQuantity: number
  closed: boolean
  // the matching day-report info (same source as the calendar) — the *effective* numbers
  extraInfo?: {
    occupiedParkingSpaces?: number
    book?: { online: number; onsite: number } | number
    maxAllowed?: number
    maxOnSite?: number
  }
}
export interface AvailabilityDayTab {
  label: string
  items: AvailabilityDayItem[]
}

export async function getAvailabilityDay(date: string): Promise<AvailabilityDayTab[]> {
  const res = await fetch(`${BASE}/actions/availabilityCalendarDay?date=${date}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  return asJson<AvailabilityDayTab[]>(res)
}

// Mirrors exactly the payload the legacy dialog posts, so server behaviour is identical.
export interface AvailabilityOverride {
  referenceId: number
  referenceType: string
  date: string
  newQuantity: string // raw input value ('' = leave the max unchanged)
  currentQuantity: number
  defaultQuantity: number
  closed: boolean
}

export async function updateAvailability(overrides: AvailabilityOverride[]): Promise<void> {
  const res = await fetch(`${BASE}/actions/availabilityUpdate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
    body: JSON.stringify(overrides),
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
}

/* ── In & Out (arrivals / departures / in-garage per day) ── */
export interface InOutTotal {
  start: string // "YYYY-MM-DD"
  total: number // sedentary (in garage that day)
  in: number
  out: number
}
export interface InOutGrid {
  total: InOutTotal[]
  detail: unknown[]
}
export async function getInOutGrid(startDate: string, endDate: string): Promise<InOutGrid> {
  const res = await fetch(`${BASE}/actions/inOutCalendarGrid?startDate=${startDate}&endDate=${endDate}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  return asJson<InOutGrid>(res)
}

export interface InOutReservation {
  code: string
  name: string
  lastName: string
  source: string
  transport: string
  licencePlate: string
  status: string
  services: string
  requireBilling: string
  arrivalDay: string
  arrivalHour: string
  leaveDay: string
  leaveHour: string
  paymentMode: string
  isIn: boolean
  isOut: boolean
}
export interface InOutRange {
  startHour: string
  endHour: string
  in: number
  out: number
}
export interface InOutDay {
  day: string
  sedentary: number
  inGarage: number
  inTotal?: number
  outTotal?: number
  ranges: InOutRange[]
  inReservations: InOutReservation[]
  outReservations: InOutReservation[]
  sedentaryReservations: InOutReservation[]
}
export async function getInOutDay(date: string): Promise<InOutDay> {
  const res = await fetch(`${BASE}/actions/inOutCalendarDay?date=${date}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  return asJson<InOutDay>(res)
}
/** Day export — Excel (full) or PDF (filter: 'inout' | 'sedentary'). */
export function inOutExportUrl(date: string, format: 'excel' | 'pdf', filter?: 'inout' | 'sedentary'): string {
  const p = new URLSearchParams({ date, export: format })
  if (filter) p.set('filter', filter)
  return `${BASE}/actions/inOutCalendarDay?${p.toString()}`
}

/* ── Rate calendar — per-day special prices (overrides) on rates ── */
export interface RateCalTransport {
  label: string
  count: number
  overrideCount: number
}
export interface RateCalDay {
  date: string
  transports: RateCalTransport[]
}
export async function getRateCalendarGrid(startDate: string, endDate: string): Promise<RateCalDay[]> {
  const res = await fetch(`${BASE}/actions/rateCalendarGrid?startDate=${startDate}&endDate=${endDate}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  return asJson<RateCalDay[]>(res)
}

export interface RateCalRate {
  id: string
  label: string
  price: number // base price (cents)
  enabled: boolean // override enabled
  specialPrice: number // override price for this day (cents; 0 = none)
}
export interface RateCalDayGroup {
  label: string // transport name
  rates: RateCalRate[]
}
export async function getRateCalendarDay(date: string): Promise<RateCalDayGroup[]> {
  const res = await fetch(`${BASE}/actions/rateCalendarDay?date=${date}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  return asJson<RateCalDayGroup[]>(res)
}

// Mirrors RateOverrideUpdateAction: per rate, set/replace the override for `date` (which the
// server appends "T00:00:00Z" to). price is integer cents.
export interface RateOverridePatch {
  id: string
  date: string // "YYYY-MM-DD"
  price: number // cents
  enabled: boolean
}
export async function updateRateOverrides(rows: RateOverridePatch[]): Promise<void> {
  const res = await fetch(`${BASE}/actions/rateOverrideUpdate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
    body: JSON.stringify(rows),
  })
  if (res.status === 401) notifyUnauthorized()
  if (!res.ok) {
    const raw = await res.text().catch(() => '')
    let msg = `HTTP ${res.status}`
    try {
      const j = JSON.parse(raw) as { message?: string }
      if (j?.message) msg = j.message
    } catch {
      if (raw) msg = `HTTP ${res.status}: ${raw.slice(0, 200)}`
    }
    throw new ApiError(msg, res.status)
  }
}

/* ── Application / audit logs (gsmLog) ──
   Mirrors the legacy LogController: list the log files, then fetch the tail of one.
   Endpoints: GET /admin/rest/command/log  and  /admin/rest/command/log/{id}?lines=N. */
export interface LogFileRef {
  id: string // file name, e.g. "garagesanmarco-2026-06-08.log"
}

export async function getLogList(): Promise<LogFileRef[]> {
  const res = await fetch(`${BASE}/rest/command/log`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (res.status === 401) notifyUnauthorized()
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  const arr = await asJson<unknown[]>(res)
  // Tolerate either [{id}] or ["name"].
  return (Array.isArray(arr) ? arr : []).map((x) =>
    typeof x === 'string' ? { id: x } : { id: String((x as { id?: unknown }).id ?? '') },
  )
}

/** The tail (`lines`) of a single log file, as raw text. */
export async function getLogText(id: string, lines: number): Promise<string> {
  const nid = id.replace(/\.log$/i, '')
  const res = await fetch(`${BASE}/rest/command/log/${encodeURIComponent(nid)}?lines=${lines}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
    cache: 'no-store',
  })
  if (res.status === 401) notifyUnauthorized()
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  const text = await res.text()
  // The endpoint returns {data: "..."} JSON, but fall back to the raw body.
  try {
    const j = JSON.parse(text) as { data?: unknown }
    if (j && typeof j === 'object' && 'data' in j) return String(j.data ?? '')
  } catch {
    /* not JSON — use as-is */
  }
  return text
}

/** Direct download URL for a full log file. */
export function logDownloadUrl(id: string, lines = 1000): string {
  const nid = id.replace(/\.log$/i, '')
  return `${BASE}/rest/command/log/${encodeURIComponent(nid)}?lines=${lines}`
}
